import React from 'react';
import Link from 'next/link';
import { IMorressierEvent } from 'typings/event';
import { isWithinInterval, isBefore } from 'date-fns';
import { faSignalStream } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  EventDuration,
  EventStatus,
  UserEventBody,
  Title,
  UserEventDateContainer,
  UserEventAnchor,
  EventContainer,
  StyledLiveStatus
} from './style';
import { logEvent } from 'utils/eventTracking';
import { getDateRange } from '@morressier/ts';

type EventType = 'HOSTING' | 'ATTENDING' | 'REVIEWING' | '';

type UserEventProps = {
  type: EventType;
  event: IMorressierEvent;
  moduleName?: string;
};

type TUserEvent = IMorressierEvent & {
  roles?: (
    | 'organizer'
    | 'author'
    | 'reviewer'
    | 'paper-reviewer'
    | 'section_editor'
  )[];
};

// Roles can see Manage link in sidebar
const authorizedRoles = [
  'organizer',
  'section_editor',
  'reviewer',
  'paper-reviewer'
];

const UserEvent = (props: UserEventProps) => {
  const { event, type, moduleName } = props;
  const title = event?.name || `Untitled ${moduleName} Event`;
  const startDateTime = event?.start_date;
  const endDateTime = event?.end_date;
  const ppmEnabled = event?.poster_presentation_management_enabled;
  const manageBtnLeftMargin = ppmEnabled ? 'manage' : 'view';
  const isBetweenInterval = () => {
    try {
      return isWithinInterval(new Date(), {
        start: new Date(startDateTime),
        end: new Date(endDateTime)
      });
    } catch (err) {
      return false;
    }
  };
  const getEventStatus = () => {
    if (isBefore(new Date(), new Date(startDateTime))) {
      return 'upcoming';
    } else if (isBetweenInterval()) {
      return 'live';
    }
    return '';
  };
  const eventStatus = getEventStatus();

  const getDuration = () => {
    const startDate = new Date(startDateTime);
    const endDate = new Date(endDateTime);
    return getDateRange({ startDate, endDate });
  };

  const isUserRoleIsAllowed = (event: TUserEvent) => {
    const foundRole = event?.roles?.find(role =>
      authorizedRoles.includes(role)
    );
    return Boolean(foundRole);
  };

  // Note: Below function is also used in shared-ts, may be we can export that function to reuse it here
  // also make sure we can pass dynamic component for rendering Manage link in this function.
  const getManageLink = (event: TUserEvent, moduleName?: string) => {
    const isValidRole = isUserRoleIsAllowed(event);
    if (!isValidRole) return null;

    let href = '';

    if (event?.roles?.includes('organizer')) {
      href = `/conference/event/${event.id}`;
    } else if (event?.roles?.includes('section_editor')) {
      href = `/conference/event/${event.id}`;
    } else if (event?.roles?.includes('reviewer')) {
      href = `/conference/event/${event.id}/abm/review`;
    } else if (event?.roles?.includes('paper-reviewer')) {
      href = `/conference/event/${event.id}/prc/review`;
    } else {
      href = `/event/${event.id}`;
    }

    return (
      <Link
        data-test-id={`activity-sidebar-${event.short_name ?? event.id}-manage`}
        key="edit-link"
        href={href}
        passHref={true}
      >
        <UserEventAnchor
          state={manageBtnLeftMargin}
          onClick={() =>
            logEvent(
              moduleName
                ? `USER_SIDEBAR_CONTINUE_NEW_${moduleName}_SETUP_CLICKED`
                : 'HOME_MY_EVENTS_CLICKED',
              {
                action: 'manage',
                id: event.id,
                selectedOption: moduleName
              }
            )
          }
        >
          {moduleName ? 'Continue Event' : 'Manage'}
        </UserEventAnchor>
      </Link>
    );
  };

  const getReviewLink = (event: TUserEvent) => {
    let href = ``;

    if (event?.roles?.includes('reviewer')) {
      href = `/conference/event/${event.id}/abm/review`;
    } else if (event?.roles?.includes('paper-reviewer')) {
      href = `/conference/event/${event.id}/prc/review`;
    } else return null;

    return (
      <Link
        data-test-id={`activity-sidebar-${event.short_name ?? event.id}-manage`}
        key="edit-link"
        href={href}
        passHref={true}
      >
        <UserEventAnchor
          state={manageBtnLeftMargin}
          onClick={() =>
            logEvent('HOME_MY_EVENTS_CLICKED', {
              action: 'review',
              id: event.id
            })
          }
        >
          Review
        </UserEventAnchor>
      </Link>
    );
  };

  return (
    <EventContainer
      className={
        type === 'HOSTING' ? 'test-Hosting-Event' : 'test-Attending-Event'
      }
    >
      <Title>{title}</Title>
      <UserEventDateContainer>
        <EventDuration>{getDuration()}</EventDuration>
        {eventStatus === 'live' ? (
          <StyledLiveStatus>
            <FontAwesomeIcon
              icon={faSignalStream}
              color="var(--color-red-50)"
              style={{ marginRight: '4px' }}
            />
            <EventStatus status={eventStatus}>{eventStatus} </EventStatus>
          </StyledLiveStatus>
        ) : (
          <EventStatus status={eventStatus}>{eventStatus} </EventStatus>
        )}
      </UserEventDateContainer>
      <UserEventBody>
        {ppmEnabled && (
          <Link key="view-link" href={`/o/event/${event.id}`} passHref={true}>
            <UserEventAnchor
              state={'view'}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                logEvent('HOME_MY_EVENTS_CLICKED', {
                  action: 'view',
                  id: event.id
                })
              }
            >
              View
            </UserEventAnchor>
          </Link>
        )}
        {type === 'HOSTING' && getManageLink(props.event, moduleName)}
        {type === 'REVIEWING' && getReviewLink(props.event)}
      </UserEventBody>
    </EventContainer>
  );
};

export default UserEvent;
